import cn from 'classnames';
import { FC, useCallback, useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Select } from '@/shared/ui';

import {
  DocumentTemplateSummary,
  EncounterSchema,
  SchemaDocumentRef,
} from '@/entities/templates';

import { transcriptorRecordingMessages } from '@/features/audioRecording';

import { TranscriptorRecordingFormData } from '../../config';

import styles from './TranscriptorSelectionFields.module.scss';

type TranscriptorSelectionFieldsProps = {
  control: Control<TranscriptorRecordingFormData>;
  schemas: EncounterSchema[];
  documentTemplates: DocumentTemplateSummary[];
  schemasLoading: boolean;
  selectedSchemaId: string;
  selectedTemplateSeriesIds: string[];
  selectMenuPortalTarget: HTMLElement | null;
  onSchemaChange: (schemaId: string) => void;
  onTemplatesChange: (templateSeriesIds: string[]) => void;
};

export const TranscriptorSelectionFields: FC<
  TranscriptorSelectionFieldsProps
> = (props) => {
  const {
    control,
    schemas,
    documentTemplates,
    schemasLoading,
    selectedSchemaId,
    selectedTemplateSeriesIds,
    selectMenuPortalTarget,
    onSchemaChange,
    onTemplatesChange,
  } = props;

  const { formatMessage } = useIntl();

  const schemaOptions = useMemo(
    () =>
      schemas.map((schema) => ({
        value: schema.id,
        label: schema.name,
      })),
    [schemas],
  );

  const selectedSchema = useMemo(
    () => schemas.find((schema) => schema.id === selectedSchemaId),
    [schemas, selectedSchemaId],
  );

  const documentTemplateBySeriesId = useMemo(
    () =>
      new Map(
        documentTemplates
          .filter((template) => template.series_id)
          .map((template) => [template.series_id, template]),
      ),
    [documentTemplates],
  );

  const getDocumentLabel = useCallback(
    (doc: SchemaDocumentRef) => {
      const template = doc.template_series_id
        ? documentTemplateBySeriesId.get(doc.template_series_id)
        : undefined;

      return (
        doc.tab_title ||
        doc.name ||
        template?.tab_title ||
        template?.name ||
        doc.code ||
        template?.code ||
        doc.template_series_id ||
        ''
      );
    },
    [documentTemplateBySeriesId],
  );

  const onDemandTemplateOptions = useMemo(() => {
    if (!selectedSchema?.documents) {
      return [];
    }

    return selectedSchema.documents
      .filter(
        (doc) =>
          doc.inclusion === 'on_demand' && Boolean(doc.template_series_id),
      )
      .map((doc) => ({
        value: doc.template_series_id as string,
        label: getDocumentLabel(doc),
      }));
  }, [selectedSchema, getDocumentLabel]);

  const requiredTemplateLabels = useMemo(() => {
    if (!selectedSchema?.documents) {
      return [];
    }

    return selectedSchema.documents
      .filter((doc) => doc.inclusion === 'always')
      .map((doc) => getDocumentLabel(doc));
  }, [selectedSchema, getDocumentLabel]);

  const hasOnDemandTemplates = onDemandTemplateOptions.length > 0;
  const hasRequiredTemplates = requiredTemplateLabels.length > 0;

  return (
    <>
      <div className={styles.section}>
        <Controller
          name="schemaId"
          control={control}
          render={({ field }) => (
            <Select
              className={styles.select}
              label={formatMessage(
                transcriptorRecordingMessages.specialtySelectionTitle,
              )}
              value={field.value}
              isSearchable={false}
              isDisabled={schemasLoading || schemas.length === 0}
              menuPortalTarget={selectMenuPortalTarget ?? undefined}
              menuPosition={selectMenuPortalTarget ? 'fixed' : undefined}
              onChange={(value) => {
                if (typeof value === 'string') {
                  field.onChange(value);
                  onSchemaChange(value);
                }
              }}
              options={schemaOptions}
              placeholder={formatMessage(
                schemas.length === 0
                  ? transcriptorRecordingMessages.specialtySelectionEmpty
                  : transcriptorRecordingMessages.specialtySelectionPlaceholder,
              )}
            />
          )}
        />
      </div>

      {hasRequiredTemplates && (
        <div className={styles.section}>
          <span className={cn('p4', styles.requiredTemplatesLabel)}>
            {formatMessage(
              transcriptorRecordingMessages.requiredTemplatesTitle,
            )}
          </span>
          <ul className={styles.requiredTemplatesList}>
            {requiredTemplateLabels.map((label) => (
              <li
                key={label}
                className={cn('p3', styles.requiredTemplatesItem)}
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.section}>
        <Controller
          name="templateSeriesIds"
          control={control}
          render={({ field }) => (
            <Select
              className={styles.select}
              label={formatMessage(
                transcriptorRecordingMessages.templatesSelectionTitle,
              )}
              isMulti
              value={selectedTemplateSeriesIds}
              isSearchable={false}
              isDisabled={!selectedSchema || !hasOnDemandTemplates}
              menuPortalTarget={selectMenuPortalTarget ?? undefined}
              menuPosition={selectMenuPortalTarget ? 'fixed' : undefined}
              onChange={(value) => {
                const next = Array.isArray(value)
                  ? (value as string[])
                  : value
                    ? [value as string]
                    : [];
                field.onChange(next);
                onTemplatesChange(next);
              }}
              options={onDemandTemplateOptions}
              placeholder={formatMessage(
                hasOnDemandTemplates
                  ? transcriptorRecordingMessages.templatesSelectionPlaceholder
                  : transcriptorRecordingMessages.templatesSelectionEmpty,
              )}
            />
          )}
        />
      </div>
    </>
  );
};
